import { useCallback } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { getEmptyFormValues, useFormAPI } from "@/widgets/classic-form/form";
import { populateFormValuesFromMetadata } from "@/widgets/classic-form/metadata-converters/populateFormValuesFromMetadata";
import { useMetadataQuery } from "@/entities/form-metadata";
import { useResetValidationErrors } from "@/shared/forms";
import { usePageContextQuery } from "@/shared/page-context/usePageContextQuery";
import { useUICustomization } from "@/shared/ui-customization";
import { getButtonWidthClass } from "../sharedCustomizations/getButtonWidthClass";

interface ClearProps {
    disabled: boolean;
    label?: string;
}

export const Clear = ({ label, disabled }: ClearProps) => {
    const { t } = useTranslation();
    const formApi = useFormAPI();
    const { data: formMetadata } = useMetadataQuery();
    const { data: pageContext } = usePageContextQuery();
    const resetValidationErrors = useResetValidationErrors();
    const { controlButtonsWidth } = useUICustomization();
    const defaultYearProximity = pageContext.runtimeConfig.defaultYearProximity;

    const clearForm = useCallback(() => {
        const clearedFormValues = populateFormValuesFromMetadata({
            isEditForm: false,
            formMetadata,
            sourceFormValues: getEmptyFormValues(),
            matchAllTermsExactly: false,
            defaultYearProximity,
        });
        formApi.reset(clearedFormValues);
        resetValidationErrors();
    }, [formApi, formMetadata, resetValidationErrors, defaultYearProximity]);

    return (
        <button
            onClick={clearForm}
            className={classNames("ancBtn silver", getButtonWidthClass(controlButtonsWidth))}
            type="button"
            disabled={disabled}
            data-testid="clear-form"
        >
            {label ?? t("searchForm.clear")}
        </button>
    );
};
